import { Component } from "react";
import { observer } from "mobx-react";

import { isNotificationEnabled } from "@formant/core/model/IBaseEvent";
import { IEvent } from "@formant/core/model/IEvent";
import { ILabelingRequestData } from "@formant/core/model/ILabelingRequestData";
import { IPhysicalRequestData } from "@formant/core/model/IPhysicalRequestData";
import { ISelectionRequestData } from "@formant/core/model/ISelectionRequestData";
import { ITeleopRequestData } from "@formant/core/model/ITeleopRequestData";
import { Uuid } from "@formant/core/model/Uuid";

import {
    annotationTemplateStore,
    commandStore,
    commandTemplateStore,
    userStore,
} from "../../stores";
import { formatUserDisplayName } from "../../utils/formatUserDisplayName";

export interface IEventHeadlineProps {
    event: IEvent;
}

const isRequestDataWithTitle = (
    it:
        | ISelectionRequestData
        | ILabelingRequestData
        | ITeleopRequestData
        | IPhysicalRequestData,
): it is ISelectionRequestData | ILabelingRequestData => "title" in it;

@observer
export class EventHeadline extends Component<IEventHeadlineProps> {
    public render() {
        const { event } = this.props;
        if (
            event.type === "datapoint-event" ||
            event.type === "triggered-event" ||
            event.type === "device-online" ||
            event.type === "device-offline" ||
            event.type === "stateful"
        ) {
            if (isNotificationEnabled(event)) {
                return event.severity;
            } else {
                return "event";
            }
        }

        if (event.type === "intervention-request") {
            const responded = (event.responses?.length || 0) > 0;
            const requestStatus = responded ? "closed" : "open";
            return isRequestDataWithTitle(event.data) &&
                event.data.title &&
                event.data.title.length > 0
                ? `${requestStatus} - ${event.data.title}`
                : requestStatus;
        }

        if (
            event.type === "command-request" ||
            event.type === "command-delivery" ||
            event.type === "command-response"
        ) {
            const { commandId } = event;
            return commandIdToName(commandId) || false;
        }

        if (event.type === "teleop-session-record") {
            return "Teleoperation";
        }

        if (event.type === "port-forwarding-session-record") {
            return "Port forwarding";
        }

        if (event.type === "custom") {
            if (isNotificationEnabled(event) && event.severity) {
                return event.severity;
            }
            return "Custom API Event";
        }

        if (event.type === "comment") {
            const { userId } = event;
            const user = userStore.get(userId);
            if (!user) {
                return false;
            }
            return formatUserDisplayName(user);
        }

        if (event.type === "annotation") {
            const { userId, annotationTemplateId } = event;
            const user = userStore.get(userId);
            if (!user) {
                return false;
            }
            let result = formatUserDisplayName(user);
            const annotationTemplate =
                annotationTemplateId &&
                annotationTemplateStore.get(annotationTemplateId);
            if (annotationTemplate) {
                result += `/${annotationTemplate.name}`;
            }
            return result;
        }

        if (event.type === "system") {
            return "System";
        }

        return false;
    }
}

function commandIdToName(commandId: Uuid): string | undefined {
    const command = commandStore.get(commandId);
    if (command) {
        const { commandTemplateId } = command;
        return commandTemplateId
            ? commandTemplateIdToName(commandTemplateId)
            : command.command;
    }
}

function commandTemplateIdToName(commandTemplateId: Uuid): string | undefined {
    const commandTemplate = commandTemplateStore.get(commandTemplateId);
    if (commandTemplate) {
        return commandTemplate.name;
    }
}
